import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### FHA Monthly MIP (Mortgage Insurance Premium)
 *
 * The FHA MIP report communicates premiums for FHA-insured loans. The report itself is a newline-separated txt file
 * that can contain multiple loans (and multiple lenders, but we won't need that). The FHAMonthlyMIP type represents
 * the entire report; the individual loans will be represented as an array where each loan is represented as a
 * "Type 4 Supplemental Record" line (a line that starts with the character 4).
 *
 * The string formatter that creates the final txt file should take this type as input and return the newline-separated string.
 * This type can also be stored as a record on the investor reporting table. Note that we are skipping fields on the report
 * that are going to have fixed values (e.g. the "company number" field of the header record which is always `00502`). The
 * values in this type are limited to those we actually need to compute per company and per loan, since there is a ton of
 * redundant/filler info.
 *
 * Refer to this table for the string formatting requirements: https://www.hud.gov/hud-partners/housing-premium-monthly-mip-batch-formats-new
 *
 * Basic structure of the report:
 * - Header record
 * - Detail record (lender info)
 * - Supplemental records (one line per loan)
 * - Last supplemental record (indicates the end of the list)
 * - Deposit control record (sum of the # of records above and the total $ amount being reported)
 * - Deposit total record (includes the total amount of records in this file)
 *
 * Because we will only ever be reporting a single lender, we will expect to have one header record before we get to
 * the list of loan records.
 */
export const FHAMonthlyMIPV1 = z.object({
  // For the header record:
  transmissionDateAndTime: zodDateOrString.describe('date'),

  // For the detail record (lender info):
  fhaLenderId: z.string().describe('string'),
  totalTransactionAmount: z.number().describe('currency'),

  // Supplemental records:
  records: z.array(
    z.object({
      fhaCaseNumber: z.string().describe('string'),
      dollarAmount: z.number().describe('currency'),
      loanNumber: z.string().describe('string'),
    }),
  ),
});
export type FHAMonthlyMIPV1 = z.infer<typeof FHAMonthlyMIPV1>;

export const FHAMonthlyMIP = FHAMonthlyMIPV1;
export type FHAMonthlyMIP = z.infer<typeof FHAMonthlyMIP>;
// Make `FHAMonthlyMIP` a discriminated union on 'version' when changes happen
// export const FHAMonthlyMIPV2 = z.object({
//   version: z.literal('2'),
//   foo: z.string(),
// });
// export const FHAMonthlyMIP = z.discriminatedUnion('version', [FHAMonthlyMIPV1, FHAMonthlyMIPV02012025]);
